'use client';

import { useState } from 'react';
import { Mail, Clock, Check, MessageCircle, Heart } from 'lucide-react';
import { EmailLink } from '@/components/EmailLink';

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: '', // Honeypot field - should stay empty
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
      } else {
        setError(data.message || 'Er is iets misgegaan. Probeer het later opnieuw.');
      }
    } catch {
      setError('Er is iets misgegaan. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <>
      {/* Hero with Form */}
      <section className="bg-gradient-to-br from-cta via-cta-light to-cta pt-32 pb-16 text-white relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-20 -left-10 w-32 h-32 bg-white/5 rounded-full" />
        <div className="absolute bottom-20 -right-10 w-40 h-40 bg-yellow-400/10 rounded-full" />
        <div className="absolute top-1/2 right-20 w-20 h-20 bg-white/5 rounded-full hidden lg:block" />

        <div className="container-site relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Neem contact op
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Heeft u vragen over de ValBewust app? We helpen u graag persoonlijk verder.
              </p>

              {/* Contact Info Cards */}
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <Mail className="w-8 h-8 text-yellow-300 mb-3" />
                  <div className="text-lg text-white/80">E-mail</div>
                  <EmailLink name="info" className="text-white font-semibold text-lg hover:text-yellow-300 transition-colors" />
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <Clock className="w-8 h-8 text-yellow-300 mb-3" />
                  <div className="text-lg text-white/80">Reactietijd</div>
                  <div className="text-white font-semibold text-lg">1-2 werkdagen</div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-cta/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-cta" />
                  </div>
                  <h2 className="text-2xl font-bold text-ink mb-3">Bedankt voor uw bericht!</h2>
                  <p className="text-lg text-ink/70">We nemen zo snel mogelijk contact met u op.</p>
                </div>
              ) : (
                <>
                  <h2 className="font-bold text-2xl mb-6 text-cta">Stuur ons een bericht</h2>
                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                      {error}
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-5 relative">
                    {/* Honeypot field - hidden from humans, visible to bots */}
                    <div className="absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <label htmlFor="name" className="block text-base font-medium text-ink mb-2">
                        Uw naam *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-4 text-lg text-ink bg-white rounded-xl border-2 border-ink/20 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-base font-medium text-ink mb-2">
                        E-mailadres *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-4 text-lg text-ink bg-white rounded-xl border-2 border-ink/20 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-base font-medium text-ink mb-2">
                        Telefoonnummer
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-4 text-lg text-ink bg-white rounded-xl border-2 border-ink/20 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-base font-medium text-ink mb-2">
                        Onderwerp *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-4 text-lg text-ink bg-white rounded-xl border-2 border-ink/20 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                      >
                        <option value="">Selecteer een onderwerp</option>
                        <option value="app">Vraag over de app</option>
                        <option value="hulp">Hulp nodig</option>
                        <option value="feedback">Feedback geven</option>
                        <option value="anders">Anders</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-base font-medium text-ink mb-2">
                        Uw bericht *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-4 text-lg text-ink bg-white rounded-xl border-2 border-ink/20 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Verzenden...' : 'Verstuur bericht'}
                    </button>

                    <p className="text-sm text-ink/60 mt-4">
                      Door te verzenden gaat u akkoord met onze{' '}
                      <a href="/privacy" className="underline hover:text-cta">Privacyverklaring</a>.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="section-padding bg-gradient-to-br from-cta/5 to-cta/10 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-10 -left-10 w-28 h-28 bg-cta/10 rounded-full" />
        <div className="absolute bottom-10 -right-10 w-24 h-24 bg-yellow-400/20 rounded-full" />

        <div className="container-site relative">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-ink text-center mb-12">
              Wij staan voor u klaar
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <div className="w-14 h-14 bg-cta/10 rounded-2xl flex items-center justify-center mb-6">
                  <Heart className="w-7 h-7 text-cta" />
                </div>
                <h3 className="font-bold text-xl mb-3 text-ink">Persoonlijke aandacht</h3>
                <p className="text-ink/70 text-lg">
                  We nemen de tijd om uw vraag goed te begrijpen en u zo goed mogelijk te helpen.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <div className="w-14 h-14 bg-cta/10 rounded-2xl flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-cta" />
                </div>
                <h3 className="font-bold text-xl mb-3 text-ink">Snelle reactie</h3>
                <p className="text-ink/70 text-lg">
                  We streven ernaar om binnen 1-2 werkdagen te reageren op uw bericht.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <div className="w-14 h-14 bg-cta/10 rounded-2xl flex items-center justify-center mb-6">
                  <MessageCircle className="w-7 h-7 text-cta" />
                </div>
                <h3 className="font-bold text-xl mb-3 text-ink">Duidelijke uitleg</h3>
                <p className="text-ink/70 text-lg">
                  We leggen alles helder uit, zonder moeilijke termen of vakjargon.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
